// Различия рабочего стола, планшета и браузера в одном месте.
//
// ## Зачем этот слой
//
// Программу заказчик получает как приложение (ПРАВИЛА.md, решение 7), с 10.09.2026 ещё и как
// кабинет для планшета, а разрабатываем и проверяем мы её в браузере. Один и тот же код рисует
// все три окна — и три действия ведут себя по-разному:
//
// | Действие | В браузере | В окне программы | На планшете |
// |---|---|---|---|
// | сохранить выгрузку | ссылка с `download` по адресу `blob:` | диалог «сохранить как» и запись файла | запись к себе и системный лист «Поделиться» |
// | напечатать | `window.print()` | команда окна: в webview на macOS печать из JS не открывается вовсе | команда плагина: печатью на планшете заведует система |
// | сообщить о находке сторожа | вкладка браузера | системное уведомление, видное при свёрнутом окне | ничего: разрешение спрашивать не за что |
//
// Ветка выбирается ОДИН раз при запуске и по признаку от самой обёртки, а не по наличию
// внутренних переменных Tauri: они меняются от версии к версии, и проверка «есть ли объект
// __TAURI__» однажды тихо перестанет срабатывать — выгрузка при этом просто перестанет
// сохранять файл, без единой ошибки на экране.
//
// ## Почему перечисление, а не признак «это десктоп»
//
// До планшета здесь стоял `десктоп(): boolean`, и на планшете он вернул бы `true`: тот же
// Tauri, та же команда. Дальше выгрузка ушла бы в диалог сохранения, которого на планшете нет
// в том виде, в каком его ждёт код, а печать — в команду, которой там не существует. Признак
// не умеет отвечать «третье», и разбор молча уходит в неверную ветку.
//
// Перечисление заставляет разобрать ВСЕ случаи, и проверяет это компилятор.

/** Где мы работаем. `браузер` — обычная вкладка, в том числе при разработке и проверках. */
export type Платформа = 'windows' | 'macos' | 'ipad' | 'linux' | 'браузер';

let гдеМы: Promise<Платформа> | null = null;

async function определитьПлатформу(): Promise<Платформа> {
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    return await invoke<Платформа>('платформа');
  } catch {
    // Браузер: команды нет, и это нормальный, а не ошибочный путь.
    return 'браузер';
  }
}

/**
 * Где работаем. Ответ кэшируется: он не меняется за время работы.
 *
 * Кэшируется ОБЕЩАНИЕ, а не готовое значение: при запуске платформу спрашивают сразу несколько
 * мест (самообновление, проверка версии в магазине, выгрузка), и каждое, придя первым, ходило бы
 * в обёртку само. Лишние вызовы безвредны, но один из них мог бы сорваться и навсегда записать
 * «браузер» поверх верного ответа.
 */
export function платформа(): Promise<Платформа> {
  гдеМы ??= определитьПлатформу();
  return гдеМы;
}

/**
 * Работаем ли внутри окна программы НА КОМПЬЮТЕРЕ.
 *
 * Планшет сюда не входит намеренно: всё, что спрашивает этот признак, — про рабочий стол
 * (самообновление). Обновление приложения на планшете делает магазин, и плагина обновления там
 * нет вовсе — вопрос «десктоп ли мы» для него равнозначен вопросу «есть ли обновление».
 */
export async function десктоп(): Promise<boolean> {
  const где = await платформа();
  return где === 'windows' || где === 'macos' || где === 'linux';
}

/**
 * Сохранить файл под именем, которое выбрал человек.
 *
 * В браузере — обычное скачивание. В окне программы — диалог сохранения и запись байтов;
 * человек сам говорит, куда положить, и файл не теряется в общей папке загрузок. На планшете
 * чужих папок не существует: файл пишется в свой каталог и уходит в системный лист
 * «Поделиться» — оттуда его кладут в «Файлы», отправляют почтой или сразу печатают.
 *
 * Возвращает `false`, если человек закрыл диалог или лист, не выбрав место: это не ошибка, и
 * говорить ему «не выгрузилось» в таком случае нельзя.
 */
export async function сохранитьФайл(blob: Blob, имя: string): Promise<boolean> {
  const где = await платформа();

  if (где === 'ipad') {
    // Через диалог сохранения этот путь НЕ идёт, хотя команда там есть и код бы собрался.
    // Реализация диалога на планшете создаёт ПУСТОЙ файл и отдаёт его наружу, а запись байтов
    // после этого уходит мимо песочницы: человек увидел бы «файл сохранён» и получил пустую
    // книгу Excel. Для акта сверки это худший вид отказа — тихий и правдоподобный.
    const { writeFile, BaseDirectory } = await import('@tauri-apps/plugin-fs');
    await writeFile(имя, new Uint8Array(await blob.arrayBuffer()), {
      baseDir: BaseDirectory.AppCache,
    });
    const { appCacheDir, join } = await import('@tauri-apps/api/path');
    const { invoke } = await import('@tauri-apps/api/core');
    const путь = await join(await appCacheDir(), имя);
    // ⚠️ Аргументы мобильной команды заворачиваются в `payload`, и иначе она не вызывается
    // вовсе: «invalid args `payload` for command `share`». Сборка при этом зелёная, тест на
    // шов тоже — он проверял ровно тот вызов, который написан здесь. Поймано живым нажатием
    // 11.09.2026: выгрузка на планшете не работала НИ РАЗУ с самого её появления.
    return await invoke<boolean>('plugin:barkios|share', { payload: { path: путь } });
  }

  if (где !== 'браузер') {
    const { save } = await import('@tauri-apps/plugin-dialog');
    const путь = await save({
      defaultPath: имя,
      filters: [{ name: 'Книга Excel', extensions: ['xlsx'] }],
    });
    if (путь === null) {
      return false;
    }
    const { writeFile } = await import('@tauri-apps/plugin-fs');
    await writeFile(путь, new Uint8Array(await blob.arrayBuffer()));
    return true;
  }

  const адрес = URL.createObjectURL(blob);
  const ссылка = document.createElement('a');
  ссылка.href = адрес;
  ссылка.download = имя;
  document.body.appendChild(ссылка);
  ссылка.click();
  ссылка.remove();
  URL.revokeObjectURL(адрес);
  return true;
}

/** Напечатать то, что на экране. Печатная форма ведомости и акт сверки идут этим путём. */
export async function напечатать(): Promise<void> {
  const где = await платформа();
  const { invoke } = await import('@tauri-apps/api/core');

  if (где === 'ipad') {
    // На планшете печатью заведует система, и `window.print()` здесь молча ничего не делает:
    // тихий отказ вместо диалога печати. Печатная вёрстка (`@media print`) при этом
    // соблюдается — плагин печатает ту же страницу, что видно на экране.
    await invoke('plugin:barkios|print');
    return;
  }

  if (где !== 'браузер') {
    await invoke('печать_окна');
    return;
  }

  window.print();
}

/**
 * Показать уведомление на рабочем столе.
 *
 * Сторож находит расхождение в деньгах ровно тогда, когда программа не на переднем плане —
 * иначе человек увидел бы его сам. Поэтому уведомление системное, а не полоска внутри окна.
 *
 * В браузере оно молча пропускается: разрешение там спрашивают у человека, а спрашивать его
 * посреди работы ради служебного сообщения нельзя. На планшете — тоже: разрешение на
 * уведомления там спрашивают один раз и навсегда, а показывать нам пока нечего.
 */
export async function уведомить(заголовок: string, текст: string): Promise<boolean> {
  if (!(await десктоп())) {
    return false;
  }
  const { isPermissionGranted, requestPermission, sendNotification } =
    await import('@tauri-apps/plugin-notification');
  let разрешено = await isPermissionGranted();
  if (!разрешено) {
    разрешено = (await requestPermission()) === 'granted';
  }
  if (!разрешено) {
    return false;
  }
  sendNotification({ title: заголовок, body: текст });
  return true;
}
